import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Circle,
  FileSearch,
  FileText,
  Fingerprint,
  type LucideIcon,
  PenLine,
  ScanFace,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';

export interface SidebarStep {
  id: string;
  displayName: string;
  status: string; // Pending | Passed | Review | Failed
}

function iconFor(label: string): LucideIcon {
  const l = label.toLowerCase();
  if (l.includes('aadhaar')) return Fingerprint;
  if (l.includes('face') || l.includes('liveness')) return ScanFace;
  if (l.includes('bank')) return Building2;
  if (l.includes('gst')) return Building2;
  if (l.includes('kra') || l.includes('check')) return FileSearch;
  if (l.includes('sign')) return PenLine;
  return FileText;
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'Passed':
      return <CheckCircle2 size={18} className="text-[#00C232]" />;
    case 'Review':
      return <AlertCircle size={18} className="text-[#DA7400]" />;
    case 'Failed':
      return <XCircle size={18} className="text-[#BF3636]" />;
    default:
      return <Circle size={18} className="text-[#C4C4C4]" />;
  }
}

export function SessionStepSidebar({
  steps,
  activeIndex,
  onStepClick,
}: {
  steps?: SidebarStep[];
  /** Index of the currently displayed step (highlight). */
  activeIndex?: number;
  /** Called when a step row is clicked — parent updates its step index. */
  onStepClick?: (index: number) => void;
}) {
  if (!steps || steps.length === 0) return null;

  return (
    <div className="flex h-full flex-col overflow-y-auto rounded-xl border border-[#E4E4E4] bg-white p-[clamp(10px,0.8vw,12px)]">
      <p className="mb-3 px-1 text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">
        Steps
      </p>
      <div className="relative flex flex-col gap-[clamp(12px,1vw,16px)]">
        {steps.map((step, index) => {
          const Icon = iconFor(step.displayName);
          const isActive = activeIndex === index;
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => onStepClick?.(index)}
              className={cn(
                'flex w-full cursor-pointer items-center gap-2 rounded-lg border p-3 text-left transition-colors',
                isActive
                  ? 'border-[#0048FF] bg-[#EEF2FF]'
                  : 'border-[#E4E4E4] bg-white hover:bg-[#F9FAFB]',
              )}
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                <Icon size={15} className={isActive ? 'text-[#0048FF]' : 'text-[#6B7280]'} />
              </span>
              <span
                className={cn(
                  'flex-1 text-[length:var(--text-14)]',
                  isActive ? 'font-medium text-[#0048FF]' : 'font-normal text-[#666666]',
                )}
              >
                {step.displayName}
              </span>
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                <StatusIcon status={step.status} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
